import React from "react";

export function PaperHero(): JSX.Element {
  return (
    <section className="relative isolate overflow-hidden rounded-2xl sm:rounded-3xl border border-white/10 bg-slate-900/40 p-5 shadow-2xl shadow-black/50 backdrop-blur-sm sm:p-10">
      {/* Ambient background: grid + animated glows */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-2xl sm:rounded-3xl">
        <div className="absolute top-0 left-1/4 h-[200px] w-[200px] sm:h-[500px] sm:w-[500px] -translate-y-1/2 rounded-full bg-blue-600/20 blur-[60px] sm:blur-[100px]" />
        <div className="absolute bottom-0 right-1/4 h-[200px] w-[200px] sm:h-[500px] sm:w-[500px] translate-y-1/2 rounded-full bg-cyan-500/10 blur-[60px] sm:blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] [background-size:24px_24px] sm:[background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)]" />
      </div>

      {/* Top bar / mini navbar */}
      <div className="mb-8 sm:mb-12 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/20">
            {/* Simple vector logo placeholder if image fails, or just wrapping the img */}
            <img
              src={`${import.meta.env.BASE_URL}textbook2videologo_inverted.svg`}
              alt="Logo"
              className="h-6 w-6 text-white"
              onError={(e) => {
                  // Fallback if inverted logo doesn't exist
                  const target = e.target as HTMLImageElement;
                  const parent = target.parentElement;
                  if (!parent) {
                    return;
                  }
                  target.style.display = "none";
                  parent.innerText = "T2V";
                  parent.className =
                    "flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 font-bold text-white";
              }}
            />
          </div>
          <span className="text-lg font-bold tracking-tight text-white">Textbook2Video</span>
        </div>
        <nav className="flex flex-wrap items-center gap-2 sm:gap-3">
          <a href="#gallery" className="nav-pill">Gallery</a>
          <a href="#how-it-works" className="nav-pill">How It Works</a>
          <a href="#docs" className="nav-pill">Docs</a>
          <a
            href="https://github.com/jakekinchen/textbook2video"
            target="_blank"
            rel="noreferrer"
            className="nav-pill !border-slate-700 !bg-slate-800 !text-white hover:!bg-slate-700"
          >
            GitHub
          </a>
        </nav>
      </div>

      {/* Hero Content */}
      <div className="grid grid-cols-1 items-center gap-8 sm:gap-12 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <h1 className="animate-fade-in bg-gradient-to-r from-white via-cyan-100 to-blue-200 bg-clip-text text-3xl font-bold leading-tight tracking-tight text-transparent sm:text-5xl md:text-6xl">
            Textbooks to Videos. <br />
            <span className="text-cyan-400">Instantly.</span>
          </h1>
          <p className="animate-fade-in mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-400 delay-100">
            Transform dense instructional PDFs into engaging, narrated explainer videos. 
            Powered by AI-driven storyboarding, programmatic animation, and neural TTS.
          </p>

          {/* Badges / actions */}
          <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 sm:gap-4 animate-fade-in delay-200">
            <a href="#generate" className="btn-primary w-full sm:w-auto text-center">Start Generating</a>
            <a href="#gallery" className="btn-secondary w-full sm:w-auto text-center">View Gallery</a>
            
            <div className="hidden sm:flex ml-2 items-center gap-2 rounded-full border border-cyan-900/50 bg-cyan-950/30 px-3 py-1 text-xs font-medium text-cyan-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              System Online
            </div>
          </div>
        </div>

        {/* Stats / Abstract Card */}
        <aside className="glass-card lg:col-span-2 animate-fade-in delay-300 transform transition-all hover:scale-[1.01] mt-4 lg:mt-0">
            <div className="flex items-center justify-between border-b border-white/5 pb-4 mb-4">
                <span className="text-xs font-bold uppercase tracking-widest text-cyan-500">System Architecture</span>
                <div className="flex gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-red-500/50"></div>
                    <div className="h-2 w-2 rounded-full bg-yellow-500/50"></div>
                    <div className="h-2 w-2 rounded-full bg-green-500/50"></div>
                </div>
            </div>
            
            <div className="space-y-4">
                <div className="flex items-start gap-3">
                   <div className="mt-1 rounded bg-blue-500/10 p-1.5 text-blue-400 flex-shrink-0">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                   </div>
                   <div>
                       <h3 className="text-sm font-medium text-slate-200">Input Processing</h3>
                       <p className="text-xs text-slate-500">PDF Parsing, OCR & Segmentation</p>
                   </div>
                </div>
                <div className="flex items-start gap-3">
                   <div className="mt-1 rounded bg-purple-500/10 p-1.5 text-purple-400 flex-shrink-0">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
                   </div>
                   <div>
                       <h3 className="text-sm font-medium text-slate-200">Generation Engine</h3>
                       <p className="text-xs text-slate-500">LLM Storyboarding & Manim Code Gen</p>
                   </div>
                </div>
                <div className="flex items-start gap-3">
                   <div className="mt-1 rounded bg-green-500/10 p-1.5 text-green-400 flex-shrink-0">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                   </div>
                   <div>
                       <h3 className="text-sm font-medium text-slate-200">Rendering & TTS</h3>
                       <p className="text-xs text-slate-500">Parallel Rendering & Audio Mix</p>
                   </div>
                </div>
            </div>
        </aside>
      </div>
    </section>
  );
}
